/**
 * This component displays all the properties linked to a specific owner
 * in a table format using AG Grid with server-side pagination.
 */
import { type ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useState } from 'react';
import { Loader } from 'src/components/atoms/Loader';
import { ShowState } from 'src/components/molecules/ShowState';
import { PaginationControls } from 'src/components/organisms/PaginationControls';
import useOwnerProperties, {
  IOwnerPropertiesResult
} from 'src/features/owner/hooks/useOwnerProperties';
import { gridStyle } from './styles';

interface IOwnerPropertiesTableProps {
  ownerId: string;
}

interface IPropertyRowData {
  propertyName: string;
  companyName: string;
  address: string;
}

const transformPropertiesToRowData = (properties: IOwnerPropertiesResult[]): IPropertyRowData[] => {
  return properties.map((property) => {
    const { name, company_name, address, city, state, zip } = property;

    return {
      propertyName: name || 'N/A',
      companyName: company_name || 'N/A',
      address: `${address}, ${city}, ${state} ${zip}`
    };
  });
};

const OwnerPropertiesTable = ({ ownerId }: IOwnerPropertiesTableProps) => {
  const [rowData, setRowData] = useState<IPropertyRowData[]>([]);

  // Column Definitions: Defines the columns to be displayed.
  const colDefs: ColDef[] = [
    { field: 'propertyName', headerName: 'Property Name', flex: 1 },
    { field: 'companyName', headerName: 'Company Name', flex: 1 },
    { field: 'address', headerName: 'Address', flex: 2 }
  ];

  const {
    properties,
    isLoading,
    error,
    currentPage,
    rowsPerPage,
    totalCount,
    totalPages,
    fetchProperties,
    setPageSize,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage
  } = useOwnerProperties(ownerId);

  // Transform properties to row data when properties change
  useEffect(() => {
    if (properties && properties.length > 0) {
      const transformedData = transformPropertiesToRowData(properties);
      setRowData(transformedData);
    } else if (!isLoading) {
      setRowData([]);
    }
  }, [properties, isLoading]);

  // Loading state
  if (isLoading && properties.length === 0) {
    return <Loader />;
  }

  // Error state
  if (error) {
    return (
      <ShowState
        variant="error"
        type="information"
        message="Failed to load owner properties"
        buttonLabel="Retry"
        onClick={() => fetchProperties({ page: 0 })}
      />
    );
  }

  // Empty state
  if (!isLoading && properties.length === 0) {
    return (
      <ShowState
        variant="empty"
        type="information"
        message="This owner has no properties linked."
      />
    );
  }

  return (
    <div>
      <div className="ag-theme-alpine" style={gridStyle}>
        <AgGridReact
          suppressRowHoverHighlight={true}
          suppressCellFocus={true}
          rowData={rowData}
          columnDefs={colDefs}
          loading={isLoading}
          pagination={true}
          paginationPageSize={rowsPerPage}
          paginationPageSizeSelector={[10, 25, 50]}
          suppressPaginationPanel={true}
        />
      </div>

      <PaginationControls
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        totalPages={totalPages}
        setPageSize={setPageSize}
        fetchNextPage={fetchNextPage}
        fetchPreviousPage={fetchPreviousPage}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        isLoading={isLoading}
        itemDescriptor="properties"
      />
    </div>
  );
};

export default OwnerPropertiesTable;
